/**
 * CameraPassthrough — owns the live camera `<video>` element.
 *
 * The element is mounted HIDDEN (visibility:hidden, off-screen position)
 * because we no longer draw it to the DOM. Rendering the camera is a
 * per-scene concern — a game that wants the camera as a background
 * layer calls `SceneCamera.attach(scene)` (see `ui/SceneCamera.js`),
 * which reads this element and draws it into the Phaser scene.
 *
 * The same `<video>` is exposed via `getVideoElement()` so the tracking
 * subsystem can read frames from it without spawning a second
 * `getUserMedia` request.
 *
 * Why hidden instead of visible in the DOM:
 *   - Menu scenes shouldn't show the camera (only the game scene should)
 *   - DOM layering under Phaser's canvas is fragile (body backgrounds,
 *     stacking contexts, letterbox bars all interfere)
 *   - Scene-level rendering gets automatic cleanup on scene teardown
 *
 * Graceful behavior:
 *   - In Node (no `document`/`navigator.mediaDevices`), `start()` is a no-op
 *     that resolves with `false` so AR-aware games don't crash in tests.
 *   - If permission is denied or no camera exists, `onError` fires and
 *     `isStarted` stays false.
 */
export class CameraPassthrough {
  /**
   * @param {object} [opts]
   * @param {'environment'|'user'} [opts.facingMode='environment']
   * @param {boolean} [opts.mirror] - default true for 'user', false for 'environment'
   * @param {(err: Error) => void} [opts.onError]
   */
  constructor(opts = {}) {
    this.facingMode = opts.facingMode || 'environment';
    this.mirror = opts.mirror == null ? this.facingMode === 'user' : !!opts.mirror;
    this.onError = opts.onError || null;

    this.video = null;
    this.stream = null;
    this.isStarted = false;
  }

  /**
   * Request the camera and mount a HIDDEN `<video>`. Returns true if a
   * stream is playing, false if it isn't (denied / unsupported / Node).
   */
  async start() {
    if (this.isStarted && this.video) return true;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      if (this.onError) this.onError(new Error('camera unsupported'));
      return false;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.facingMode },
        audio: false
      });
    } catch (err) {
      if (this.onError) this.onError(err);
      return false;
    }

    if (typeof document === 'undefined') return false;

    this.video = document.createElement('video');
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', 'true');
    this.video.srcObject = this.stream;

    // Hidden but still active. display:none stops playback in some
    // browsers, so use visibility + off-screen position instead. Tracking
    // and texture readback still work because videoWidth/videoHeight
    // stay valid and we can drawImage() the element onto a canvas.
    Object.assign(this.video.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      pointerEvents: 'none',
      visibility: 'hidden'
    });

    document.body.appendChild(this.video);

    try {
      await this.video.play();
    } catch (err) {
      // Some browsers reject play() until a user gesture. The element is
      // still attached and will start playing on the next tap.
      if (this.onError) this.onError(err);
    }

    this.isStarted = true;
    return true;
  }

  /** Release the camera stream and remove the element. */
  stop() {
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        try { track.stop(); } catch (_) { /* ignore */ }
      }
      this.stream = null;
    }
    if (this.video && this.video.parentNode) {
      this.video.parentNode.removeChild(this.video);
    }
    this.video = null;
    this.isStarted = false;
  }

  /** The mounted `<video>` element, or null. Used by tracking + SceneCamera. */
  getVideoElement() {
    return this.video;
  }

  /** Frame dimensions reported by the camera, or null if not started. */
  getDimensions() {
    if (!this.video) return null;
    return {
      width: this.video.videoWidth || 0,
      height: this.video.videoHeight || 0
    };
  }
}
